import React, { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { Box } from '~/eds/src/components/Box/Box';
import { Button } from '~/eds/src/components/Button/Button';
import { Chip } from '~/eds/src/components/Chip/Chip';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Select } from '~/eds/src/components/Select/Select';
import { Text } from '~/eds/src/components/Text/Text';
import { StatusType } from '~/eds/src/types/StatusType';
import { api } from '~/redux/api/createApi';
import { GuidelineClauseVariation } from '~/redux/api/types/playbooks/guidelines';
import HtmlContent from '~/ui/components/HtmlContent/HtmlContent';

type Props = {
  onInsertVariation: (variation: GuidelineClauseVariation) => void;
};

export const ImportVariation = ({ onInsertVariation }: Props) => {
  const intl = useIntl();
  const { toast } = useToast();
  const [state, setState] = React.useState({
    clauseType: '',
    clauseVariation: '',
  });

  const {
    data: clauses,
    error: clauseSummariesError,
    isFetching: clauseSummariesLoading,
  } = api.endpoints.clauseLibraryListClauses.useQuery({ filters: {} });

  const {
    data: clauseDetails,
    error: clauseDetailsError,
    isFetching: clauseDetailsLoading,
  } = api.endpoints.clauseLibraryGetClause.useQuery(
    {
      clauseId: state.clauseType,
    },
    {
      skip: !state.clauseType,
    },
  );

  const clauseOptions = useMemo(
    () =>
      clauses
        ? clauses.map((clause) => ({
            label: clause.name,
            value: clause.key,
            tooltip: clause.name,
          }))
        : [],
    [clauses],
  );
  const clauseVariationOptions = useMemo(() => {
    const selectedClause = clauses?.find(
      (clause) => clause.key === state.clauseType,
    );
    return (
      selectedClause?.children.map((variation) => ({
        label: variation.name,
        value: variation.key,
        tooltip: variation.name,
      })) ?? []
    );
  }, [clauses, state.clauseType]);

  const variation = clauseDetails?.variations?.find(
    (v) => v.key === state.clauseVariation,
  );

  const chipStatus: Record<string, StatusType> = {
    Preferred: 'success',
    Fallback: 'warning',
    'Walk away': 'danger',
  };

  useEffect(() => {
    if (clauseSummariesError || clauseDetailsError) {
      toast({
        status: 'danger',
        title: intl.formatMessage({
          id: 'VariationForm.clauseSummariesErrorTitle',
          defaultMessage: 'Error loading clause summaries',
          description:
            'Title for the error toast when clause summaries fail to load in the clause variation form',
        }),
        message: intl.formatMessage({
          id: 'VariationForm.clauseSummariesErrorMessage',
          defaultMessage:
            'There was an issue fetching clause summaries. Please try again later.',
          description:
            'Message for the error toast when clause summaries fail to load in the clause variation form',
        }),
      });
    }
  }, [clauseSummariesError, clauseDetailsError]);

  return (
    <Layout direction="column" spacing={3}>
      <Text>
        {intl.formatMessage({
          id: 'ImportVariation.instructions',
          defaultMessage:
            'Add an existing Clause Variation from the pre-approved Clause Library. The same clause can be linked to multiple Playbooks.',
          description: 'instructions for importing a clause variation',
        })}
      </Text>
      <FormField
        label={intl.formatMessage({
          id: 'ImportVariation.clauseType',
          defaultMessage: 'Clause Type',
          description: 'label for clause type import field',
        })}
        input={Select}
        inputProps={{
          options: clauseOptions,
          isLoading: clauseSummariesLoading,
        }}
        name="clauseType"
        value={state.clauseType}
        onChange={(value) =>
          setState({
            clauseType: value as string,
            clauseVariation: '',
          })
        }
      />
      <FormField
        label={intl.formatMessage({
          id: 'ImportVariation.clauseVariation',
          defaultMessage: 'Clause Variation',
          description: 'label for clause variation import field',
        })}
        input={Select}
        inputProps={{
          options: clauseVariationOptions,
          isLoading: clauseSummariesLoading || clauseDetailsLoading,
        }}
        name="clauseVariation"
        value={state.clauseVariation}
        onChange={(value) =>
          setState((prev) => ({ ...prev, clauseVariation: value as string }))
        }
      />
      {variation && (
        <Layout direction="column" spacing={4}>
          <Layout spacing={2} align="center">
            <Text variant="body-bold">{variation.name}</Text>
            <Chip
              mode="tag"
              status={chipStatus[variation.position]}
              text={variation.position}
            />
          </Layout>
          <Box bg="background.info" p={3} borderRadius={4}>
            <HtmlContent html={variation.text ?? ''} />
          </Box>

          <Text>
            <HtmlContent html={variation.guidance ?? ''} />
          </Text>

          <Layout justify="flex-end" mt={3}>
            <Button
              disabled={!variation}
              variant="secondary"
              text={intl.formatMessage({
                id: 'ImportVariation.importButton',
                defaultMessage: 'Insert Clause Variation',
                description: 'Button label to import clause variation',
              })}
              onClick={() =>
                onInsertVariation({
                  ...variation,
                  clause_variation_id: variation.key,
                })
              }
            />
          </Layout>
        </Layout>
      )}
    </Layout>
  );
};
